import React, { useState } from 'react'
import { Box, Typography, Button } from '@mui/material'
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount'
import useAccount from '../../hooks/useAccount'
import ManagerPasswordPromptDialog from '../admin/ManagerPasswordPromptDialog'

// Fixed height (px) of the banner when visible. Layout offsets the AppBar and
// the main content by this amount when impersonation is active so the banner
// doesn't overlap the header chrome.
export const IMPERSONATION_BANNER_HEIGHT = 36

// Fixed-position banner shown whenever the current session was started via
// manager impersonation. The "Stop" button re-authenticates the manager
// (identified by actor_email on the current JWT) to return to their own
// account.
//
// Kept visually distinct from the primary app chrome so it can't be mistaken
// for normal state — the whole point is unmistakable audit visibility.
const ImpersonationBanner: React.FC = () => {
  const account = useAccount()
  const [stopOpen, setStopOpen] = useState(false)

  if (!account.isImpersonating || !account.user) return null

  const targetEmail = account.user.session_info?.email || account.user.session_info?.name || ''
  const managerEmail = account.actorEmail || ''

  const handleStop = async (password: string) => {
    await account.onStopImpersonating(password)
  }

  return (
    <>
      <Box
        sx={{
          backgroundColor: '#7c4dff',
          color: 'white',
          px: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 2,
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          height: `${IMPERSONATION_BANNER_HEIGHT}px`,
          zIndex: (theme) => theme.zIndex.appBar + 2,
          boxShadow: '0 2px 4px rgba(0,0,0,0.15)',
        }}
        role="status"
        aria-live="polite"
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <SupervisorAccountIcon fontSize="small" />
          <Typography variant="body2" sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            Impersonating <strong>{targetEmail}</strong>
            {managerEmail ? <> (as <strong>{managerEmail}</strong>)</> : null}
          </Typography>
        </Box>
        <Button
          size="small"
          variant="outlined"
          onClick={() => setStopOpen(true)}
          sx={{
            color: 'white',
            borderColor: 'rgba(255,255,255,0.6)',
            '&:hover': { borderColor: 'white', backgroundColor: 'rgba(255,255,255,0.08)' },
            flexShrink: 0,
          }}
        >
          Stop impersonating
        </Button>
      </Box>
      <ManagerPasswordPromptDialog
        open={stopOpen}
        title="Return to your account"
        description={`Enter your password to sign back in as ${managerEmail}.`}
        confirmLabel="Sign back in"
        onClose={() => setStopOpen(false)}
        onConfirm={handleStop}
      />
    </>
  )
}

export default ImpersonationBanner
